import { useEffect, useMemo, useState } from 'react'
import InvoiceReportTable from '../components/InvoiceTables/InvoiceReportTable'
import { useQuery } from 'react-query'
import { fetchInvoiceReport, fetchReportTermLimits } from '../../../../api/newApi'
import { useChosenCompany, useUser, useUserContext } from '../../../../context/UserProvider'
import { ConsolidateInvoiceSelect } from '../../../cars/components/V3/_shared/ConsolidateInvoiceSelect'
import type { Invoice } from '../../../../types'
import { subOneYearMonth, todayDateTime, valueToDate } from '../../../../utils/helpers/helpers.reports'
import { DatePicker } from '../../../../components/DatePicker'
import { Input } from '../../../cars/components/V3/_shared/Form/Input'
import { SearchIcon } from '../../../cars/components/V3/SearchIcon'
import { getConsolidateInvoiceDescription } from '../../../../utils/getConsolidateInvoiceDescription'
import { formatDate } from '../../../../utils/tableFuncs'

const clampDate = (value: string, min: string, max: string) => {
  const valueTime = new Date(value).getTime()
  const minTime = new Date(min).getTime()
  const maxTime = new Date(max).getTime()

  if (valueTime < minTime) return min
  if (valueTime > maxTime) return max
  return value
}

const InvoiceReport = () => {
  const company = useChosenCompany()
  const companyId = company?.id
  const contactId = useUser()?.user?.id
  const userContext = useUserContext() as any
  const consolidatedAgreementID = userContext?.consolidateInvoice?.ConsolidateCode as any
  const chosenCompany = userContext?.chosenCompany as string | undefined

  const defaultToDate = useMemo(() => todayDateTime(), [])
  const defaultFromDate = useMemo(() => subOneYearMonth(defaultToDate, 'date'), [defaultToDate])

  const [toDate, setToDate] = useState(defaultToDate)
  const [fromDate, setFromDate] = useState(defaultFromDate)
  const [dateRangeReadyKey, setDateRangeReadyKey] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const dateRangeKey = companyId && contactId ? `${companyId}:${contactId}` : null

  const { data: termLimits, status: termLimitsStatus } = useQuery(
    ['invoiceReportTermLimits', companyId, contactId],
    () => fetchReportTermLimits({ companyId, contactId, type: 'invoice' }),
    {
      enabled: !!companyId && !!contactId,
    },
  ) as { data?: { oldest: string; newest: string }; status: 'idle' | 'loading' | 'error' | 'success' }

  const oldestDate = termLimits?.oldest
  const newestDate = termLimits?.newest

  useEffect(() => {
    if (termLimitsStatus === 'loading' || termLimitsStatus === 'idle') return

    if (!oldestDate || !newestDate || termLimitsStatus === 'error') {
      setToDate(defaultToDate)
      setFromDate(defaultFromDate)
      setDateRangeReadyKey(dateRangeKey)
      return
    }

    const nextToDate = clampDate(toDate, oldestDate, newestDate)
    const nextDefaultFromDate = subOneYearMonth(nextToDate, 'date')
    const nextFromDate = clampDate(
      fromDate === defaultFromDate || fromDate > nextToDate ? nextDefaultFromDate : fromDate,
      oldestDate,
      nextToDate,
    )

    if (nextToDate !== toDate) setToDate(nextToDate)
    if (nextFromDate !== fromDate) setFromDate(nextFromDate)
    setDateRangeReadyKey(dateRangeKey)
  }, [dateRangeKey, defaultFromDate, defaultToDate, fromDate, newestDate, oldestDate, termLimitsStatus, toDate])

  const { data: response, status } = useQuery(
    ['invoiceReport', companyId, contactId, fromDate, toDate, consolidatedAgreementID],
    () => fetchInvoiceReport({ companyId, contactId, fromDate, toDate, fetchAll: true, consolidatedAgreementID }),
    {
      enabled: !!companyId && !!contactId && dateRangeReadyKey === dateRangeKey,
    },
  )

  //@ts-expect-error axios type issue
  const items = (response?.items && Array.isArray(response.items) ? response.items : []) as Invoice[]
  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return items

    return items.filter((item) => {
      const searchableFields = [
        item.consolidatedInvoiceNo,
        item.consolidatedInvoiceCode,
        getConsolidateInvoiceDescription(item.consolidatedInvoiceCode, chosenCompany),
        formatDate(item.postingDate),
        item.consolidatedInvoiceDescription,
        item.billToCustomerName,
      ]

      return searchableFields.some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      )
    })
  }, [chosenCompany, items, search])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-xl font-medium text-primaryText">Fakturarapport</span>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 w-full">
          <div className="relative w-full md:w-80">
            <Input
              id="invoice-report-search"
              type="search"
              value={search}
              placeholder="Søk"
              className="h-11 pl-11"
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="absolute top-[14px] left-[14px]">
              <SearchIcon />
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end gap-2 w-full md:w-auto">
            <div className="w-full md:w-auto">
              <DatePicker
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                minDate={oldestDate ? valueToDate(oldestDate, 'date') : undefined}
                maxDate={newestDate ? valueToDate(newestDate, 'date') : undefined}
              />
            </div>
            <div className="flex-1 md:flex-none">
              <ConsolidateInvoiceSelect className="h-11 w-full" />
            </div>
          </div>
        </div>
      </div>

      <InvoiceReportTable isLoading={status === 'loading'} data={filteredItems} />
    </div>
  )
}

export default InvoiceReport
